import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { collection, getDocs, setDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { FaVideo, FaCode, FaFont, FaFileAlt, FaImage, FaLink, FaUpload, FaRegFilePdf } from 'react-icons/fa';
import Notification from '../../component/notification';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

async function addAssignment(courseId, day, assignmentData, type) {
  const assignmentsRef = collection(db, 'courses', courseId, 'day', `day ${day}`, 'assignments');
  const snapshot = await getDocs(assignmentsRef);
  let maxNum = 0;
  snapshot.docs.forEach(docSnap => {
    const match = docSnap.id.match(/assignment (\d+)/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxNum) maxNum = num;
    }
  });
  const newId = `assignment ${maxNum + 1}`;
  const assignmentDocRef = doc(db, 'courses', courseId, 'day', `day ${day}`, 'assignments', newId);
  await setDoc(assignmentDocRef, {
    ...assignmentData,
    type,
    createdDate: new Date().toISOString()
  });
}



const StudentContentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const course = location.state?.course;
  const day = location.state?.day;
  const isEdit = Boolean(location.state?.editData && location.state.editData.id);
  const [assignmentType, setAssignmentType] = useState(location.state?.editData?.type || "");
  // Add these imports at the top
 
  
  // Modify the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    let errs = {};
    if (!form.title) errs.title = "Required";
    if (!form.description) errs.description = "Required";
    if (!form.thumbnail) errs.thumbnail = "Required";
    if (!form.documentUrl) errs.documentUrl = "Required";
    if (!form.videoLink) errs.videoLink = "Required";
    if (!form.file && !isEdit) errs.file = "Please select a file to upload";
  
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      setLoading(true);
      setNotification({ visible: true, message: isEdit ? "Updating..." : "Submitting...", type: "loading" });
      try {
        let fileURL = form.fileURL;
        let storagePath = form.storagePath;
  
        if (form.file) {
          const storage = getStorage();
          storagePath = `courses/${course.id}/day/${day}/assignments/${form.file.name}`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, form.file);
          fileURL = await getDownloadURL(storageRef);
        }
  
        const assignmentData = {
          title: form.title,
          description: form.description,
          thumbnail: form.thumbnail,
          documentUrl: form.documentUrl,
          videoLink: form.videoLink,
          fileURL: fileURL,
          storagePath: storagePath,
          updatedDate: new Date().toISOString()
        };
  
        if (isEdit) {
          const assignmentDocRef = doc(db, 'courses', course.id, 'day', `day ${day}`, 'assignments', location.state.editData.id);
          await updateDoc(assignmentDocRef, assignmentData);
        } else {
          await addAssignment(course.id, day, assignmentData, assignmentType);
        }
  
        setNotification({ visible: true, message: isEdit ? "✅ Content updated successfully!" : "✅ Content added successfully!", type: "success" });
        navigate('/coursecontent', { state: { course, day, contentAdded: true } });
      } catch (error) {
        console.error('Content submission error:', error);
        setNotification({ visible: true, message: isEdit ? `❗ Failed to update content: ${error.message}` : `❗ Failed to add content: ${error.message}`, type: "failure" });
      } finally {
        setLoading(false);
      }
    } else {
      setNotification({ visible: true, message: "⚠️ Please fill in all required fields.", type: "warning" });
    }
    setTimeout(() => setNotification({ visible: false, message: "", type: "" }), 3000);
  };
  
  // Update the initial form state to include fileURL and storagePath
  const [form, setForm] = useState({
    title: location.state?.editData?.title || "",
    description: location.state?.editData?.description || "",
    thumbnail: location.state?.editData?.thumbnail || "",
    documentUrl: location.state?.editData?.documentUrl || "",
    file: null,
    videoLink: location.state?.editData?.videoLink || "",
    fileURL: location.state?.editData?.fileURL || "",
    storagePath: location.state?.editData?.storagePath || ""
  });
  const [notification, setNotification] = useState({ visible: false, message: "", type: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleTypeSelect = (type) => {
    setAssignmentType(type);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file") {
      setForm(prev => ({ ...prev, file: files[0] }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // Remove assignmentType and type selection logic
  // Always render the form
  const readOnly = Boolean(location.state?.readOnly);
  return (
    <div className="border rounded shadow-sm p-4 bg-white" style={{ maxWidth: 600, margin: '2rem auto' }}>
      <Notification message={notification.message} type={notification.type} visible={notification.visible} onClose={() => setNotification({ visible: false, message: '', type: '' })} />
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontWeight: 700, fontSize: '2rem', letterSpacing: '0.5px', margin: 0 }}>{course?.title} - Day {day}</h2>
        <div className="mb-2 text-muted" style={{ fontSize: '1rem' }}>
          {readOnly ? <span style={{ color: '#7c3aed', fontWeight: 600 }}>Viewing Content (Read-Only)</span> : 'Fill in the details to add new content.'}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="container" style={{ maxWidth: 500 }}>
        <Box sx={{ mb: 2, color: '#6d28d9', fontSize: '1.1rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FaFileAlt style={{ marginRight: 8, color: '#a78bfa' }} /> Fill in the details for the assignment.
        </Box>
        <TextField
          name="title"
          label={<span><FaFileAlt style={{ color: '#a78bfa', marginRight: 6 }} /> Title</span>}
          value={form.title}
          onChange={handleChange}
          fullWidth
          disabled={readOnly}
          sx={{ mb: 3, borderRadius: 2, background: readOnly ? '#f3f4f6' : 'inherit', fontWeight: readOnly ? 600 : 400, fontSize: readOnly ? '1.1rem' : '1rem' }}
          InputProps={{ style: readOnly ? { color: '#4b5563' } : {} }}
          error={!!errors.title}
          helperText={errors.title}
        />
        <TextField
          name="description"
          label={<span><FaFileAlt style={{ color: '#a78bfa', marginRight: 6 }} /> Description</span>}
          value={form.description}
          onChange={handleChange}
          fullWidth
          multiline
          minRows={3}
          disabled={readOnly}
          sx={{ mb: 3, borderRadius: 2, background: readOnly ? '#f3f4f6' : 'inherit', fontWeight: readOnly ? 600 : 400, fontSize: readOnly ? '1.1rem' : '1rem' }}
          InputProps={{ style: readOnly ? { color: '#4b5563' } : {} }}
          error={!!errors.description}
          helperText={errors.description}
        />
        <TextField
          name="thumbnail"
          label={<span><FaImage style={{ color: '#a78bfa', marginRight: 6 }} /> Thumbnail URL</span>}
          value={form.thumbnail}
          onChange={handleChange}
          fullWidth
          disabled={readOnly}
          sx={{ mb: 3, borderRadius: 2, background: readOnly ? '#f3f4f6' : 'inherit', fontWeight: readOnly ? 600 : 400, fontSize: readOnly ? '1.1rem' : '1rem' }}
          InputProps={{ style: readOnly ? { color: '#4b5563' } : {} }}
          error={!!errors.thumbnail}
          helperText={errors.thumbnail}
        />
        <TextField
          name="documentUrl"
          label={<span><FaRegFilePdf style={{ color: '#a78bfa', marginRight: 6 }} /> Document URL</span>}
          value={form.documentUrl}
          onChange={handleChange}
          fullWidth
          disabled={readOnly}
          sx={{ mb: 3, borderRadius: 2, background: readOnly ? '#f3f4f6' : 'inherit', fontWeight: readOnly ? 600 : 400, fontSize: readOnly ? '1.1rem' : '1rem' }}
          InputProps={{ style: readOnly ? { color: '#4b5563' } : {} }}
          error={!!errors.documentUrl}
          helperText={errors.documentUrl}
        />
        <Box sx={{ mb: 3 }}>
          <label style={{ fontWeight: 500, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 6 }}><FaUpload style={{ marginRight: 8 }} /> File Upload</label><br />
          <input
            type="file"
            name="file"
            onChange={handleChange}
            style={{ marginTop: 8, borderRadius: 8, padding: 8, border: '1px solid #a78bfa', background: readOnly ? '#f3f4f6' : 'inherit' }}
            disabled={readOnly}
          />
        </Box>
        <TextField
          name="videoLink"
          label={<span><FaLink style={{ color: '#a78bfa', marginRight: 6 }} /> Video Link</span>}
          value={form.videoLink}
          onChange={handleChange}
          fullWidth
          disabled={readOnly}
          sx={{ mb: 3, borderRadius: 2, background: readOnly ? '#f3f4f6' : 'inherit', fontWeight: readOnly ? 600 : 400, fontSize: readOnly ? '1.1rem' : '1rem' }}
          InputProps={{ style: readOnly ? { color: '#4b5563' } : {} }}
          error={!!errors.videoLink}
          helperText={errors.videoLink}
        />
        
        {!readOnly && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              color="inherit"
              sx={{ borderRadius: 2, px: 4, fontWeight: 500, borderColor: '#a78bfa', color: '#7c3aed', '&:hover': { borderColor: '#7c3aed', background: '#ede9fe' } }}>
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ borderRadius: 2, px: 4, fontWeight: 500, ml: 2, background: '#a78bfa', '&:hover': { background: '#7c3aed' } }}>
              {isEdit ? "Update Content" : "Add Content"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default StudentContentForm;
